import { useState, useEffect, useRef, useCallback } from 'react';
import { isOnline, onStatusChange, getQueueSize, startMonitor, syncQueue, clearQueue } from '../../lib/offlineQueue';
import { WifiOff, RefreshCw, CheckCircle2, Wifi, GripVertical, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

// Only show the big red banner after the connection has been
// continuously down for this long. Avoids flashing on tiny blips.
const SUSTAINED_OFFLINE_MS = 10000;

const POSITION_STORAGE_KEY = 'connectionIndicator.position.v1';
// `anchor: true` means the indicator follows the user role badge in the
// sidebar so it stays in the same RELATIVE position across all screen
// sizes / browsers / DPI. This is the default; only the superadmin can
// opt out by dragging the dot somewhere else (which sets `anchor: false`
// and reverts to the legacy absolute-pixel positioning).
const DEFAULT_POSITION = { x: null, y: null, right: null, bottom: null, anchor: true };
const ROLE_BADGE_SELECTOR = '[data-testid="role-badge"]';
// Offset (in px) of the indicator FROM the role badge. Right of it,
// vertically centered. Small enough to feel "attached".
const ANCHOR_OFFSET_X = 6;
const ANCHOR_OFFSET_Y = 0;
// How often to re-pull the global position so non-superadmin users
// pick up the superadmin's changes without needing to reload.
const POSITION_REFRESH_MS = 30000;

function readSavedPosition() {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return DEFAULT_POSITION;
    const p = JSON.parse(raw);
    if (typeof p === 'object' && p !== null) return { ...DEFAULT_POSITION, ...p };
  } catch { /* ignore */ }
  return DEFAULT_POSITION;
}

function writeSavedPosition(pos) {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos));
  } catch { /* ignore */ }
}

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

export function ConnectionBanner() {
  const { admin } = useAuth();
  // Only superadmins can move the connection indicator; for everyone else
  // the drag handle is hidden and pointer events are ignored.
  const canDrag = admin?.role === 'superadmin';
  const [online, setOnline] = useState(isOnline());
  const [queueSize, setQueueSize] = useState(getQueueSize());
  const [justReconnected, setJustReconnected] = useState(false);
  const [sustainedOffline, setSustainedOffline] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [position, setPosition] = useState(readSavedPosition);
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef(null);
  const indicatorRef = useRef(null);
  const offlineTimerRef = useRef(null);

  useEffect(() => {
    startMonitor();

    const clearOfflineTimer = () => {
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
    };

    const unsub = onStatusChange((status) => {
      setOnline(status);
      setQueueSize(getQueueSize());
      if (status) {
        clearOfflineTimer();
        setSustainedOffline(false);
        setJustReconnected(true);
        setTimeout(() => setJustReconnected(false), 4000);
      } else {
        // Defer the loud red banner: only show if we stay offline for 10s
        clearOfflineTimer();
        offlineTimerRef.current = setTimeout(() => {
          setSustainedOffline(true);
        }, SUSTAINED_OFFLINE_MS);
      }
    });

    const onSync = (e) => {
      setQueueSize(e.detail.remaining);
      if (e.detail.synced > 0) {
        // Imported lazily to avoid pulling toast lib into the connection
        // monitor's critical path when no sync is happening.
        import('sonner').then(({ toast }) => {
          toast.success(`${e.detail.synced} cambio${e.detail.synced > 1 ? 's' : ''} sincronizado${e.detail.synced > 1 ? 's' : ''} con éxito`);
        });
      }
    };
    window.addEventListener('queue-synced', onSync);

    const onQueued = () => {
      setQueueSize(getQueueSize());
      import('sonner').then(({ toast }) => {
        toast.info('Sin conexión. El cambio se guardó localmente y se enviará al recuperar la red.', {
          duration: 6000,
        });
      });
    };
    window.addEventListener('offline-queued', onQueued);

    const iv = setInterval(() => setQueueSize(getQueueSize()), 5000);

    return () => {
      unsub();
      window.removeEventListener('queue-synced', onSync);
      window.removeEventListener('offline-queued', onQueued);
      clearInterval(iv);
      clearOfflineTimer();
    };
  }, []);

  // ---- Drag-to-move logic (saved per-browser in localStorage) ----
  const onPointerDown = useCallback((e) => {
    if (!canDrag) return;
    // Only initiate drag with primary button or touch
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = indicatorRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragStartRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    setDragging(true);
    try { e.target.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
  }, [canDrag]);

  const onPointerMove = useCallback((e) => {
    if (!dragging || !dragStartRef.current) return;
    e.preventDefault();
    const margin = 4;
    const w = indicatorRef.current?.offsetWidth || 28;
    const h = indicatorRef.current?.offsetHeight || 28;
    const newX = clamp(e.clientX - dragStartRef.current.offsetX, margin, window.innerWidth - w - margin);
    const newY = clamp(e.clientY - dragStartRef.current.offsetY, margin, window.innerHeight - h - margin);
    dragStartRef.current.moved = true;
    // Switch to absolute x/y, drop right/bottom anchoring AND drop the
    // role-badge anchor (user explicitly opted out by dragging).
    setPosition({ x: newX, y: newY, right: null, bottom: null, anchor: false });
  }, [dragging]);

  const onPointerUp = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    writeSavedPosition(position);
    // Push the new position to the backend so every user sees it.
    if (canDrag) {
      const payload = {
        x: position.x ?? null,
        y: position.y ?? null,
        right: position.right ?? null,
        bottom: position.bottom ?? null,
        anchor: position.anchor === true,
      };
      api.put('/ui-settings/connection-indicator', payload).catch(() => { /* silent */ });
    }
    // Tiny delay to swallow the click that follows pointer up after a drag
    setTimeout(() => { dragStartRef.current = null; }, 50);
  }, [dragging, position, canDrag]);

  useEffect(() => {
    if (!dragging) return;
    const opts = { passive: false };
    window.addEventListener('pointermove', onPointerMove, opts);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove, opts);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [dragging, onPointerMove, onPointerUp]);

  // Re-clamp position if window shrinks (rotate device, resize)
  useEffect(() => {
    const onResize = () => {
      setPosition((p) => {
        if (p.x == null || p.y == null) return p;
        const w = indicatorRef.current?.offsetWidth || 28;
        const h = indicatorRef.current?.offsetHeight || 28;
        return {
          ...p,
          x: clamp(p.x, 4, window.innerWidth - w - 4),
          y: clamp(p.y, 4, window.innerHeight - h - 4),
        };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleResetPosition = useCallback(() => {
    if (!canDrag) return;
    setPosition(DEFAULT_POSITION);
    try { localStorage.removeItem(POSITION_STORAGE_KEY); } catch { /* ignore */ }
    // Tell the backend to switch back to anchor mode for everyone.
    api.put('/ui-settings/connection-indicator', {
      x: null, y: null, right: null, bottom: null, anchor: true,
    }).catch(() => { /* silent */ });
  }, [canDrag]);

  // ---- Sync with global server position ----
  // On mount AND every POSITION_REFRESH_MS, fetch the position from the
  // backend so non-superadmin users pick up the superadmin's changes.
  // Skip while dragging to avoid jitter.
  useEffect(() => {
    let cancelled = false;
    const fetchPosition = async () => {
      try {
        const res = await api.get('/ui-settings/connection-indicator');
        if (cancelled) return;
        const data = res?.data;
        if (!data) return;
        const remote = {
          x: data.x ?? null,
          y: data.y ?? null,
          right: data.right ?? null,
          bottom: data.bottom ?? null,
          // Anchor mode is preserved across reloads. Treat a missing
          // `anchor` field on the server as "true" so legacy installs
          // benefit from the anchored behavior automatically.
          anchor: data.anchor === undefined ? true : !!data.anchor,
        };
        // If everything is null/empty AND anchor isn't explicitly true,
        // fall back to default (which is also anchored).
        const hasAny = remote.x != null || remote.y != null || remote.right != null || remote.bottom != null;
        const next = hasAny || remote.anchor ? remote : DEFAULT_POSITION;
        setPosition((prev) => {
          // Only update if changed (avoid React re-render churn)
          if (
            prev.x === next.x &&
            prev.y === next.y &&
            prev.right === next.right &&
            prev.bottom === next.bottom &&
            prev.anchor === next.anchor
          ) return prev;
          writeSavedPosition(next);
          return next;
        });
      } catch { /* offline / unauthorised — keep local cache */ }
    };
    fetchPosition();
    const iv = setInterval(() => {
      if (!dragging) fetchPosition();
    }, POSITION_REFRESH_MS);
    const onFocus = () => { if (!dragging) fetchPosition(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
    };
  }, [dragging]);

  // ---- Anchor mode: track role-badge position via getBoundingClientRect ----
  // When position.anchor === true, the indicator follows the badge DOM node
  // exactly. We recompute on:
  //   - Initial mount (when badge becomes available)
  //   - Window resize / orientation change
  //   - Sidebar scroll (the badge can scroll in/out)
  //   - Periodic poll @1 Hz (cheap insurance against missed events)
  const [anchorCoords, setAnchorCoords] = useState(null);
  useEffect(() => {
    if (!position.anchor) {
      setAnchorCoords(null);
      return;
    }
    const compute = () => {
      const badge = document.querySelector(ROLE_BADGE_SELECTOR);
      if (!badge) {
        setAnchorCoords(null);
        return;
      }
      const rect = badge.getBoundingClientRect();
      // Position the dot vertically centered relative to the badge,
      // offset to its right edge.
      const w = indicatorRef.current?.offsetHeight || 16;
      setAnchorCoords({
        left: rect.right + ANCHOR_OFFSET_X,
        top: rect.top + (rect.height / 2) - (w / 2) + ANCHOR_OFFSET_Y,
      });
    };
    compute();
    const iv = setInterval(compute, 1000);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      clearInterval(iv);
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [position.anchor]);

  // ---- Discreet status indicator (always visible) ----
  // Shows a subtle dot in the top-right corner so users always know
  // the connection is healthy without an in-your-face banner.
  let dotColor = 'bg-emerald-500';
  let dotPulse = false;
  let dotIcon = <Wifi className="h-3 w-3 text-white" />;
  let dotLabel = 'Conexión OK';

  if (!online) {
    dotColor = sustainedOffline ? 'bg-red-500' : 'bg-brand-deep';
    dotPulse = true;
    dotIcon = <WifiOff className="h-3 w-3 text-white" />;
    dotLabel = sustainedOffline
      ? 'Sin conexión al servidor'
      : 'Reintentando conexión…';
  } else if (queueSize > 0) {
    dotColor = 'bg-brand-deep';
    dotIcon = <RefreshCw className="h-3 w-3 text-white animate-spin" />;
    dotLabel = `Sincronizando ${queueSize} cambio${queueSize > 1 ? 's' : ''}`;
  }

  // Compute inline style based on anchor / saved / dragged position.
  // Priority: dragging > absolute x/y > anchored coords from role badge > corner fallback.
  const indicatorStyle = {};
  if (dragging || position.anchor !== true) {
    // Absolute pixels mode (legacy + drag)
    if (position.x != null) indicatorStyle.left = `${position.x}px`;
    else if (position.right != null) indicatorStyle.right = `${position.right}px`;
    if (position.y != null) indicatorStyle.top = `${position.y}px`;
    else if (position.bottom != null) indicatorStyle.bottom = `${position.bottom}px`;
  } else if (anchorCoords) {
    // Anchored to role badge — follows the badge across resizes/browsers
    indicatorStyle.left = `${anchorCoords.left}px`;
    indicatorStyle.top = `${anchorCoords.top}px`;
  } else {
    // Anchor mode requested but badge not in DOM (e.g. public page) →
    // fallback to bottom-left corner so the indicator is still visible.
    indicatorStyle.left = '12px';
    indicatorStyle.bottom = '12px';
  }
  if (dragging) indicatorStyle.cursor = 'grabbing';

  const indicator = (
    <div
      ref={indicatorRef}
      data-testid="connection-status-indicator"
      style={indicatorStyle}
      className={`fixed z-[9998] flex items-center gap-1.5 select-none ${dragging ? '' : 'transition-shadow'}`}
      onMouseEnter={() => !dragging && setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onDoubleClick={handleResetPosition}
    >
      {/* Drag handle — only visible for superadmin AND only on hover.
          Stays visible while dragging so the cursor lock follows correctly. */}
      {canDrag && (hovering || dragging) && (
        <button
          type="button"
          onPointerDown={onPointerDown}
          title="Arrastra para mover · doble click sobre el punto verde para resetear posición"
          data-testid="connection-status-drag-handle"
          className={`flex items-center justify-center h-5 w-3.5 rounded-sm bg-slate-200/70 hover:bg-slate-300 text-slate-500 hover:text-slate-700 animate-in fade-in zoom-in-90 duration-150 ${dragging ? 'cursor-grabbing' : 'cursor-grab'} touch-none`}
          style={{ touchAction: 'none' }}
        >
          <GripVertical className="h-3 w-3" />
        </button>
      )}
      {/* Status dot */}
      <div
        className={`relative flex items-center justify-center h-4 w-4 rounded-full ${dotColor} shadow-md ring-2 ring-white/70 transition-all opacity-70 hover:opacity-100`}
        title={dotLabel}
      >
        {dotPulse && (
          <span className={`absolute inset-0 rounded-full ${dotColor} opacity-60 animate-ping`} />
        )}
      </div>
      {hovering && !dragging && (
        <div className="bg-slate-900/90 text-white text-xs font-medium px-2.5 py-1 rounded-md shadow-lg whitespace-nowrap backdrop-blur flex items-center gap-1.5">
          {dotIcon}
          {dotLabel}
        </div>
      )}
    </div>
  );

  // ---- Banners (top, full width) ----
  // "Reconnected" toast — quick green flash
  if (justReconnected) {
    return (
      <>
        <div
          data-testid="connection-restored-banner"
          className="fixed top-0 left-0 right-0 z-[9999] bg-emerald-600 text-white px-4 py-2 flex flex-wrap items-center justify-center gap-2 text-sm font-medium shadow-lg animate-in slide-in-from-top duration-300"
          style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
        >
          <CheckCircle2 className="h-4 w-4" />
          Conexión restaurada
          {queueSize > 0 && ` — sincronizando ${queueSize} cambios pendientes...`}
        </div>
        {indicator}
      </>
    );
  }

  // Sustained outage (>10s) — show the loud red banner
  if (!online && sustainedOffline) {
    return (
      <>
        <div
          data-testid="connection-lost-banner"
          className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white px-4 py-2.5 flex flex-wrap items-center justify-center gap-2 text-sm font-medium shadow-lg animate-in slide-in-from-top duration-300"
          style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top))' }}
        >
          <WifiOff className="h-4 w-4 flex-shrink-0" />
          <span className="text-center">Sin conexión al servidor — Los cambios se guardarán localmente y se sincronizarán automáticamente al reconectar</span>
          {queueSize > 0 && (
            <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs ml-2">
              {queueSize} pendiente{queueSize > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {indicator}
      </>
    );
  }

  // Background syncing pending writes
  if (online && queueSize > 0) {
    const handleRetry = () => {
      try { syncQueue(); } catch { /* ignore */ }
      setTimeout(() => setQueueSize(getQueueSize()), 1500);
    };
    const handleDiscard = () => {
      if (window.confirm(`¿Descartar ${queueSize} cambio${queueSize > 1 ? 's' : ''} pendiente${queueSize > 1 ? 's' : ''}? Esta acción no se puede deshacer.`)) {
        try { clearQueue(); } catch { /* ignore */ }
        setQueueSize(0);
      }
    };
    return (
      <>
        <div
          data-testid="connection-syncing-banner"
          className="fixed top-0 left-0 right-0 z-[9999] bg-brand-deep text-white px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-sm font-medium shadow-lg"
          style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
        >
          <RefreshCw className="h-4 w-4 animate-spin flex-shrink-0" />
          <span>Sincronizando {queueSize} cambio{queueSize > 1 ? 's' : ''} pendiente{queueSize > 1 ? 's' : ''}...</span>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs bg-white/20 hover:bg-white/30 rounded-full px-2.5 py-0.5 transition-colors"
            data-testid="syncing-retry-btn"
          >
            Reintentar
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            className="text-xs bg-white/20 hover:bg-white/30 rounded-full px-2.5 py-0.5 transition-colors flex items-center gap-1"
            data-testid="syncing-discard-btn"
            title="Descartar cambios pendientes"
          >
            <X className="h-3 w-3" /> Descartar
          </button>
        </div>
        {indicator}
      </>
    );
  }

  // Default: only the discreet indicator (green dot)
  return indicator;
}
